'use client'

import React, { useMemo } from 'react'
import { useState } from 'react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useBrowserDevices, useGetFormilyBlockQuery, useMediaQuery } from '@segway/core'
import { useGetFooterQuery } from '@segway/core/src/services'

import { Arrow, CloseFooterIcon, Global, SegwayIcon } from '@/components/icons'
import { Link } from '@/i18n/navigation'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Input,
  Skeleton,
} from '@/ui'

import { useSubscribe } from '../../hooks/footer'

import styles from './footer.module.css'

interface FooterSubscriptionInfo {
  subtitle: string
  title: string
}

interface LinkProp {
  title: string
  link: string
  icon: string
}
interface FooterBlockItemProp {
  title: string
  item: LinkProp[]
}

const Footer = () => {
  const tCommon = useTranslations('Common')
  const [activeIndex, setActiveIndex] = useState<undefined | number>()
  const [aboutIsExpanded, setAboutIsExpanded] = useState(false)
  const [contactIsExpanded, setContactIsExpanded] = useState(false)
  const [serviceIsExpanded, setServiceIsExpanded] = useState(false)
  const [cutomerIsExpanded, setCustomerIsExpanded] = useState(false)
  const [subscribeIsExpanded, setSubscribeIsExpanded] = useState(false)
  const responsive = useMediaQuery()
  const { handleSubmit, isLoading, register, onSubmit } = useSubscribe('footer_signup')

  const { data: blockData } = useGetFormilyBlockQuery({ identify: 'subscribe_collection' })
  const { isChrome } = useBrowserDevices()

  const { data } = useGetFooterQuery({})

  const pageData = useMemo(() => {
    if (data && data?.footerBlock) {
      const footerBlock = data?.footerBlock?.block_data
      const footerLink = data?.footerLink?.block_data
      const copyright = data?.storeConfig?.copyright
      const footerSubscribe = data?.footerSubscribe?.block_data
      const footerCustomerCare = data?.footerCustomerCare?.block_data

      return {
        footerBlock: JSON.parse(footerBlock as string),
        footerLink: JSON.parse(footerLink as string),
        footerSubscribe: JSON.parse(footerSubscribe as string),
        footerCustomerCare: JSON.parse(footerCustomerCare as string),
        copyright,
      }
    }
  }, [data])

  const footerSubscriptionBlockData = useMemo(() => {
    if (blockData && blockData.formilyBlock) {
      const subscribeBlock = blockData.formilyBlock.block_data

      if (!subscribeBlock) {
        return { subtitle: '', title: '' }
      }

      const footerSubscribeBlockData: FooterSubscriptionInfo = JSON.parse(subscribeBlock)?.footer

      return footerSubscribeBlockData
    }
  }, [blockData])

  const toggleExpansion = (section: string) => {
    switch (section) {
      case 'about':
        setAboutIsExpanded(!aboutIsExpanded)

        break
      case 'contact':
        setContactIsExpanded(!contactIsExpanded)

        break
      case 'service':
        setServiceIsExpanded(!serviceIsExpanded)
        break
      case 'customer':
        setCustomerIsExpanded(!cutomerIsExpanded)
        break
      case 'subscribe':
        setSubscribeIsExpanded(!subscribeIsExpanded)
        break
      default:
        break
    }
  }

  return (
    <div id="page-footer">
      <div
        className={`m-auto flex w-full max-w-[2560px] flex-col items-center justify-center bg-[#F3F3F4] px-7 max-xl:px-4 ${isChrome ? 'max-xl:pb-[80px]' : 'max-xl:pb-[80px]'} max-xl:pt-[20px]`}>
        <div className="flex w-full flex-col xl:pt-[32px] 2xl:max-w-[1440px]">
          {/* logo */}
          <div className="h-[24px] w-[118px]">
            <div className="flex items-center justify-center gap-[7px]">
              <SegwayIcon />
            </div>
          </div>

          {/* content */}
          {responsive && responsive.xl ? (
            <div className="mt-[8px] flex flex-col xl:mt-[40px] xl:flex-row xl:justify-between">
              {/* 左 */}
              <div className="flex flex-col xl:flex-row xl:gap-[96px]">
                {/* abour segway */}
                {pageData ? (
                  pageData?.footerBlock?.data?.map((item: FooterBlockItemProp, index: number) => (
                    <div
                      className="flex flex-col border-b-[1px] border-black border-opacity-15 xl:gap-[16px] xl:border-none"
                      key={index}>
                      <div
                        className="flex items-center justify-between xl:text-[12px]"
                        onClick={() => {
                          if (index === activeIndex) {
                            setActiveIndex(undefined)
                          } else {
                            setActiveIndex(index)
                          }
                        }}>
                        <span className="font-miSansSemiBold520 leading-[16px] text-[#0F0F0F] max-xl:text-[12px] max-xl:leading-[56px]">
                          {item?.title}
                        </span>
                        <div className="xl:hidden">
                          {index === activeIndex ? (
                            <CloseFooterIcon rotate={45} />
                          ) : (
                            <CloseFooterIcon />
                          )}
                        </div>
                      </div>
                      <div
                        className={`flex-col gap-[12px] font-miSansMedium380 xl:flex xl:w-[160px] ${activeIndex === index ? 'flex' : 'hidden'} max-xl:pb-[32px]`}>
                        {item?.item?.map((item, index) => (
                          <div key={index} className="text-[12px] leading-[16px] text-[#6E6E73]">
                            <Link href={item?.link}>{item?.title}</Link>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <>
                    <Skeleton className="h-[240px] w-[200px] bg-white max-xl:mb-[20px] max-xl:h-[56px] max-xl:w-full" />
                    <Skeleton className="h-[240px] w-[200px] bg-white max-xl:mb-[20px] max-xl:h-[56px] max-xl:w-full" />
                    <Skeleton className="h-[240px] w-[200px] bg-white max-xl:mb-[20px] max-xl:h-[56px] max-xl:w-full" />
                  </>
                )}
              </div>

              {/* 右 */}
              {pageData ? (
                <div className="flex flex-col leading-[16px] xl:w-[348px] xl:gap-[32px]">
                  {/* customer care */}
                  <div className="flex flex-col border-b-[1px] border-black border-opacity-15 xl:order-2 xl:gap-[16px] xl:border-none">
                    {/* title */}
                    <div
                      className="flex items-center justify-between xl:text-[12px]"
                      onClick={() => toggleExpansion('customer')}>
                      <span className="font-miSansSemiBold520 leading-[16px] text-[#0F0F0F] max-xl:text-[14px] max-xl:leading-[56px]">
                        {pageData?.footerCustomerCare?.title ?? ''}
                      </span>
                      <div className="xl:hidden">
                        {cutomerIsExpanded ? <CloseFooterIcon rotate={45} /> : <CloseFooterIcon />}
                      </div>
                    </div>
                    {/* content */}
                    <div
                      className={`flex flex-col font-miSansMedium380 text-[12px] xl:flex xl:gap-[12px] ${cutomerIsExpanded ? 'flex' : 'hidden'} text-[#6E6E73] max-xl:pb-[32px]`}>
                      {pageData?.footerCustomerCare?.content?.map((item: string, index: number) => (
                        <div
                          className=""
                          key={index}
                          dangerouslySetInnerHTML={{ __html: item }}></div>
                      ))}
                    </div>
                  </div>
                  {/* subscribe us */}
                  <div className="flex flex-col gap-[16px] border-b-[1px] border-black border-opacity-15 max-xl:gap-0 xl:order-1 xl:border-none">
                    {/* title */}
                    <div
                      className="flex items-center justify-between text-[12px]"
                      onClick={() => toggleExpansion('subscribe')}>
                      <span className="font-miSansSemiBold520 leading-[16px] text-[#0F0F0F] max-xl:text-[14px] max-xl:leading-[56px]">
                        {footerSubscriptionBlockData?.title ?? ''}
                      </span>
                      <div className="xl:hidden">
                        {subscribeIsExpanded ? (
                          <CloseFooterIcon rotate={45} />
                        ) : (
                          <CloseFooterIcon />
                        )}
                      </div>
                    </div>
                    {/* content */}
                    <div
                      className={`flex flex-col gap-[12px] xl:flex ${subscribeIsExpanded ? 'flex' : 'hidden'}`}>
                      <div className="font-miSansMedium380 text-[12px] leading-[16px] text-[#6E6E73]">
                        {footerSubscriptionBlockData?.subtitle ?? ''}
                      </div>
                      <form
                        className="flex"
                        onSubmit={handleSubmit((data) => {
                          onSubmit(data)
                        })}
                        autoComplete="off">
                        <div className="flex-1">
                          <Input
                            {...register('email')}
                            autoComplete="off"
                            className="h-[48px] rounded-bl-[8px] rounded-br-[0px] rounded-tl-[8px] rounded-tr-[0px] border-[#aaaaab] bg-transparent placeholder:text-[#999]"
                            placeholder={tCommon('your_email')}
                          />
                        </div>
                        <Button
                          type="submit"
                          disabled={isLoading}
                          className="h-[48px] w-[51px] rounded-l-[0px] rounded-r-[8px] bg-[#000000] p-0 hover:bg-[#444446] active:bg-[#000000] disabled:bg-[#BBBBBD]">
                          <Arrow width={24} height={24} color="#ffffff" />
                        </Button>
                      </form>
                      {/* {showTerms && (
                    <div className="flex items-center">
                      {buttonClickCount === 1 ? (
                        <>
                          <input type="checkbox" />
                          <span className="ml-[8px] text-[12px] leading-[13px]">
                            I have read and accept the [
                            <a href="#" className="text-[#f33333] underline">
                              Terms of Service Name
                            </a>
                            ] and [
                            <a href="#" className="text-[#f33333] underline">
                              Terms of Service Name
                            </a>{' '}
                            ] by checking this box
                          </span>
                        </>
                      ) : (
                        <div className="flex items-center justify-center gap-[8px] text-[12px] leading-[12px] text-[#f33333]">
                          <Annotate /> Your subscription has been successful
                        </div>
                      )}
                    </div>
                  )} */}
                    </div>
                  </div>
                </div>
              ) : (
                <Skeleton className="h-[100px] w-full bg-white xl:h-[240px] xl:w-[200px]" />
              )}
            </div>
          ) : (
            <Accordion type="single" collapsible className="mt-[24px] w-full">
              {pageData?.footerBlock?.data?.map((item: FooterBlockItemProp, index: number) => (
                <AccordionItem
                  key={index}
                  value={item?.title}
                  className="group [&_.arrow]:hidden [&_svg]:hidden">
                  <AccordionTrigger className="h-[56px]">
                    <span className="text-[#0F0F0F]">{item?.title}</span>
                    <CloseFooterIcon className="group-data-[state=closed]:block" />
                    <CloseFooterIcon rotate={45} className="group-data-[state=open]:block" />
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className={`flex flex-col gap-[12px] xl:flex xl:w-[160px]`}>
                      {item?.item?.map((item, index) => (
                        <div key={index} className="text-[12px] leading-[16px] text-[#6E6E73]">
                          <Link href={item?.link}>{item?.title}</Link>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
              {pageData ? (
                <>
                  <AccordionItem
                    value="footerCustomerCare"
                    className="group [&_.arrow]:hidden [&_svg]:hidden">
                    <AccordionTrigger className="h-[56px]">
                      <span className="text-[#0F0F0F]">
                        {pageData?.footerCustomerCare?.title ?? ''}
                      </span>
                      <CloseFooterIcon className="group-data-[state=closed]:block" />
                      <CloseFooterIcon rotate={45} className="group-data-[state=open]:block" />
                    </AccordionTrigger>
                    <AccordionContent>
                      <div
                        className={`flex flex-col gap-4 text-[12px] text-[#6E6E73] xl:flex xl:gap-[12px]`}>
                        {pageData?.footerCustomerCare?.content?.map(
                          (item: string, index: number) => (
                            <div
                              className="leading-8"
                              key={index}
                              dangerouslySetInnerHTML={{ __html: item }}></div>
                          ),
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem
                    value="footerSubscribe"
                    className={`group [&_.arrow]:hidden [&_svg]:hidden ${styles.item}`}>
                    <AccordionTrigger className="h-[56px]">
                      <span className="text-[#0F0F0F]">
                        {footerSubscriptionBlockData?.title ?? ''}
                      </span>
                      <CloseFooterIcon className="group-data-[state=closed]:block" />
                      <CloseFooterIcon rotate={45} className="group-data-[state=open]:block" />
                    </AccordionTrigger>
                    <AccordionContent className="pb-0">
                      <div className={`flex flex-col gap-[16px] xl:flex`}>
                        <div className="text-[12px] leading-8 text-[#6E6E73]">
                          {footerSubscriptionBlockData?.subtitle ?? ''}
                        </div>
                        <form
                          className="flex"
                          autoComplete="off"
                          onSubmit={handleSubmit((data) => {
                            onSubmit(data)
                          })}>
                          <div className="flex-1">
                            <Input
                              {...register('email')}
                              autoComplete="off"
                              className="h-[48px] rounded-bl-[8px] rounded-br-[0px] rounded-tl-[8px] rounded-tr-[0px] border-[#bebebf] bg-transparent placeholder:text-[#999]"
                              placeholder={tCommon('your_email')}
                            />
                          </div>
                          <Button
                            variant="default"
                            type="submit"
                            disabled={isLoading}
                            className="h-[48px] w-[48px] rounded-l-[0px] rounded-r-[8px] bg-[#000000] hover:bg-[#0F0F0F] focus:bg-[#000000]">
                            <Arrow width={24} height={24} color="#ffffff" />
                          </Button>
                        </form>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </>
              ) : null}
            </Accordion>
          )}

          {/* foot */}
          <div className="xl:w-2xl flex flex-col pb-[32px] pt-[32px] leading-[16px] max-xl:pb-0 max-xl:pt-[47px]">
            {pageData?.footerLink?.payment_icons && responsive && responsive.xl && (
              <div className="relative mb-[16px] h-[24px] w-fit min-w-[332px]">
                <Image
                  src={pageData.footerLink.payment_icons}
                  alt="payment icons"
                  fill
                  className="object-cover"
                  sizes="100vw"
                />
              </div>
            )}
            {/* 上 */}
            <div className="flex flex-col xl:flex-row xl:justify-between">
              {/* icons */}
              <div className="order-1 flex items-center justify-center gap-[16px] max-xl:justify-start max-xl:gap-[10px] xl:order-2">
                {pageData?.footerLink?.social_media_links?.map((item: LinkProp, index: number) => (
                  <Link href={item.link} key={index}>
                    <Image src={item.icon} width={16} height={16} alt={`${item.title}`} />
                  </Link>
                ))}
              </div>
              {/* items */}
              <div className="order-2 mt-[20px] flex flex-col text-[12px] text-[#6E6E73] xl:mt-[24px] xl:hidden xl:flex-row xl:items-center xl:justify-between">
                {/* copyright */}
                <div className="justify-ceter order-2 flex items-center xl:order-3">
                  <div className="font-miSansRegular330">
                    {pageData?.footerLink?.copy_right?.map((item: string, index: number) => (
                      <span key={index} dangerouslySetInnerHTML={{ __html: item }}></span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="order-3 flex font-miSansRegular330 text-[12px] text-[#6E6E73] max-xl:mt-[16px] max-xl:flex-wrap max-xl:gap-[16px] max-xl:whitespace-nowrap xl:order-1 xl:gap-[16px]">
                {pageData?.footerLink?.links?.map((item: LinkProp, index: number) => (
                  <div
                    key={index}
                    className={`pr-[16px] font-miSansRegular330 ${index !== pageData?.footerLink?.links?.length - 1 ? 'after:mt-[2px] after:h-[10px] after:w-[1px] after:bg-[#00000026] after:align-middle' : ''} max-xl:flex max-xl:items-center max-xl:gap-[16px] max-xl:pr-0 max-xl:after:block`}>
                    <Link href={item?.link}> {item?.title}</Link>
                  </div>
                ))}
              </div>
            </div>
            {/* 下 */}

            <div className="flex flex-col font-miSansMedium380 text-[12px] text-[#6E6E73] max-xl:hidden xl:mt-[24px] xl:flex-row xl:justify-between">
              {/* copyright */}
              <div className="justify-ceter order-2 flex items-center font-miSansMedium380 xl:order-3">
                {pageData?.footerLink?.copy_right?.map((item: string, index: number) => (
                  <span key={index} dangerouslySetInnerHTML={{ __html: item }}></span>
                ))}
              </div>
              {/* Global */}
              <div className="justify-ceter order-4 mt-[-2px] flex h-fit items-center gap-[4px]">
                <Global /> Mexico / Spanish
              </div>
            </div>
          </div>
          {pageData?.footerLink?.payment_icons && responsive && !responsive.xl && (
            <div className="relative mt-[30px] h-[24px] w-fit min-w-[332px]">
              <Image
                src={pageData.footerLink.payment_icons}
                alt="payment icons"
                fill
                className="object-cover"
                sizes="100vw"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Footer
